import Typography from "@mui/material/Typography";
import { useExports } from "api/exports";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { statusValue } from "constants/general";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useCallback, useEffect, useMemo, useState } from "react";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { EXPORTS_LIST_ID } from "state/tables/types";
import {
  ExportDataKeys,
  ExportListItem,
  ExportStatus,
  ExportType,
  ExportTypeUnion,
} from "types/exports";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { formatNumberValue } from "utilities/numbers";
import { useStyles } from "./ExportList.styles";
import { ExportTypeTooltip } from "./ExportTypeTooltip";

export const ExportsList = () => {
  const tables = useAppSelector(selectTables);
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[EXPORTS_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const { listing, updateListing } = useListing(EXPORTS_LIST_ID);
  const appDispatch = useAppDispatch();
  const { useExportsList, downloadExport } = useExports();
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      EXPORTS_LIST_ID,
    );

    setFilters(filters);
  };
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const { data, error } = useExportsList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
    dynamicSearch,
  });

  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();

  const { message } = useMessage();
  const { notify } = useNotifications();

  const classes = useStyles();

  const headItems = useMemo(
    () =>
      createHeadItems([
        {
          headerText: message({ id: "exportId" }),
          testId: "exports-table-header-id",
          property: "exportId",
          isSortable: false,
        },
        {
          headerText: message({ id: "activity" }),
          testId: "exports-table-header-activity",
          property: "active",
          isSortable: false,
        },
        {
          headerText: message({ id: "exportType" }),
          property: "exportType",
          testId: "exports-table-header-type",
          isSortable: false,
        },
        {
          align: "right",
          headerText: message({ id: "numberOfRecords" }),
          testId: "exports-table-header-number-of-records",
          property: "numberOfRecords",
          isSortable: false,
        },
        {
          headerText: message({ id: "exportStartDate" }),
          property: "createdAt",
          isSortable: true,
          testId: "exports-table-header-start-date",
        },
        {
          headerText: message({ id: "exportEndDate" }),
          property: "exportEndDate",
          testId: "exports-table-header-end-date",
          isSortable: false,
        },
        {
          headerText: message({ id: "status" }),
          property: "status",
          testId: "exports-table-header-status",
          isSortable: false,
        },
      ]),
    [message],
  );

  const getExportStatusLabel = (status: string) => {
    switch (status) {
      case ExportStatus.ERROR:
        return "failedExport";
      case ExportStatus.DONE:
        return "successfullyExported";
      case ExportStatus.IN_PROGRESS:
        return "inProgress";
      case ExportStatus.PENDING:
        return "pending";
      default:
        return "";
    }
  };

  const getTypeLabelValue = (type: ExportTypeUnion) => {
    switch (type) {
      case ExportType.MEMBER:
        return "members";
      case ExportType.CAMPAIGN_CODE:
        return "redemptionCodes";
      case ExportType.MEMBER_SEGMENT:
        return "membersSegment";
      case ExportType.MEMBER_TIER:
        return "membersTier";
      case ExportType.REWARD_FULFILLMENT:
        return "rewardsFulfillment";
      default:
        return "";
    }
  };

  const getExportTypeValue = useCallback(
    (item: ExportListItem) => {
      const typeLabel = getTypeLabelValue(item.type);

      return (
        <div className={classes.exportTypeCell}>
          {item.status === statusValue.DONE && (
            <ExportTypeTooltip item={item} typeLabel={typeLabel} />
          )}
          <Typography className={classes.exportTypeCell}>
            {message({ id: typeLabel })}
          </Typography>
        </div>
      );
    },
    [classes, message],
  );

  const getExportValue = useCallback(
    (item: ExportListItem, key: ExportDataKeys) => {
      const currentValue =
        key === "createdAt" || key === "finishedAt"
          ? OLDate(item[key])
          : key === "type"
            ? getExportTypeValue(item)
            : key === "exportedRows"
              ? formatNumberValue(item.exportedRows)
              : item[key];

      return currentValue;
    },
    [getExportTypeValue],
  );

  const itemsList = useMemo(
    () =>
      data?.items.map((item) => ({
        cells: [
          {
            content: getExportValue(item, "exportId"),
            property: "exportId",
            dataHjSuppress: true,
          },
          {
            content: (
              <StatusValue
                status={item.status === statusValue.DONE}
                data-test-id={`export-list-item-${item.status === statusValue.DONE ? "active" : "inactive"}`}
              />
            ),
            property: "active",
            dataHjSuppress: true,
          },
          {
            content: getExportValue(item, "type"),
            property: "exportType",
            dataHjSuppress: true,
          },
          {
            align: "right",
            content: getExportValue(item, "exportedRows"),
            property: "numberOfRecords",
            dataHjSuppress: true,
          },
          {
            content: getExportValue(item, "createdAt"),
            property: "createdAt",
            dataHjSuppress: true,
          },
          {
            content: getExportValue(item, "finishedAt"),
            property: "exportEndDate",
            dataHjSuppress: true,
          },
          {
            content: message({ id: getExportStatusLabel(item.status) }),
            property: "status",
            dataHjSuppress: true,
          },
        ],
        id: item.exportId,
        metadata: {
          statusIsFailed: item.status === statusValue.ERROR,
          statusIsSucceed: item.status === statusValue.DONE,
          disabled: item.status === statusValue.ERROR,
        },
        testId: "exports-list-row",
      })),
    [message, getExportValue, data?.items],
  );

  const items = data?.items ? (itemsList as Row[]) : [];

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, EXPORTS_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, error, data]);

  return (
    <>
      <Subheader>
        <FiltersV2
          properties={[
            {
              query: "type",
              label: message({ id: "exportType" }),
              type: "export-type",
            },
            {
              query: "createdAt",
              label: message({ id: "exportDate" }),
              type: "datetime",
            },
            {
              query: "exportId",
              label: message({ id: "exportId" }),
              type: "text",
            },
            {
              query: "status",
              label: message({ id: "status" }),
              type: "export-status",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={data?.total}
          donutLabel={message({ id: "exports" })}
          data-test-id="exports-list"
        />
        <DynamicSearch
          property="type"
          properties={[
            { id: "type", label: "type" },
            { id: "status", label: "status" },
          ]}
          setDynamicSearch={setDynamicSearch}
          onSearch={() =>
            appDispatch(clearTablePage({ tableName: EXPORTS_LIST_ID }))
          }
        />
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={EXPORTS_LIST_ID}
        data-test-id="export-list"
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                downloadExport(id as string);
                notify(message({ id: "downloadExportInfo" }));
              },
              disableCondition: (item: Row) => !item.metadata.statusIsSucceed,
              label: message({ id: "download" }),
            },
          ],
        }}
        columns={data && headItems}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        rows={data && items}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        clearFilters={() => setFilters([])}
      />
    </>
  );
};
